import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Trophy, Map, Circle, Settings, Radio, Radar } from "lucide-react";
import { useInstance } from "@/context/InstanceContext";
import TacticalBadge from "./TacticalBadge";
import BrandingModal from "./BrandingModal";

const NAV = [
  { to: "/", label: "Dashboard", code: "S-1", icon: LayoutDashboard, end: true },
  { to: "/scoreboard", label: "Scoreboard", code: "S-2", icon: Trophy },
  { to: "/heatmap", label: "Heatmap", code: "S-3", icon: Map },
  { to: "/livemap", label: "Live Map", code: "S-4", icon: Radar },
];

function Logo({ branding, size = 44 }) {
  if (branding.logoUrl) {
    return (
      <img
        src={branding.logoUrl}
        alt={branding.serverName}
        style={{ width: size, height: size, objectFit: "contain" }}
      />
    );
  }
  const parts = (branding.serverName || "BOYS OPS").split(" ");
  return <TacticalBadge size={size} label={parts[0] || "BOYS"} sub={parts[1] || "OPS"} />;
}

export default function AppLayout({ children }) {
  const { instance, instances, setInstance, mapInfo, health, branding } = useInstance();
  const location = useLocation();
  const dbDown = health && health.db !== "connected";
  const [brandingOpen, setBrandingOpen] = useState(false);

  const breadcrumb =
    location.pathname === "/scoreboard" ? "OPS / SCOREBOARD"
    : location.pathname === "/heatmap" ? "OPS / HEATMAP"
    : location.pathname === "/livemap" ? "OPS / LIVE MAP"
    : "OPS / DASHBOARD";

  // Inject the wallpaper as a CSS var consumed by .app-bg
  const appBgStyle = branding.backgroundUrl
    ? { "--app-bg-image": `url(${branding.backgroundUrl})` }
    : {};

  return (
    <div className="min-h-screen text-[var(--text)] flex relative" style={appBgStyle}>
      <div className="app-bg" aria-hidden />

      {/* Sidebar */}
      <aside
        className="hidden md:flex w-60 shrink-0 flex-col border-r relative z-10"
        style={{
          borderColor: "var(--border)",
          background: "linear-gradient(180deg, rgba(20, 17, 13, 0.92) 0%, rgba(7, 6, 10, 0.96) 100%)",
          backdropFilter: "blur(2px)",
        }}
      >
        <button
          onClick={() => setBrandingOpen(true)}
          className="group px-5 py-5 border-b text-left hover:bg-[var(--surface-2)] transition"
          style={{ borderColor: "var(--border)" }}
          title="Branding bearbeiten"
        >
          <div className="flex items-center gap-3">
            <Logo branding={branding} size={48} />
            <div className="min-w-0">
              <div className="font-stencil text-lg leading-none truncate" style={{ color: "var(--text)" }}>
                {branding.serverName}
              </div>
              <div className="label-tech mt-1 truncate" style={{ color: "var(--tan-bright)" }}>
                {branding.tagline}
              </div>
            </div>
          </div>
        </button>

        <div className="px-5 pt-4 pb-2 flex items-center gap-2">
          <span className="label-tech" style={{ color: "var(--tan-bright)" }}>SECTIONS</span>
          <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
        </div>
        <nav className="flex-1 py-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 mx-3 px-3 py-2.5 text-sm border-l-2 transition ${
                    isActive
                      ? "text-[var(--tan-bright)] bg-[var(--surface-2)] border-l-[var(--amber-bright)]"
                      : "text-[var(--text-muted)] border-l-transparent hover:text-[var(--text)] hover:bg-[var(--surface)]"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="font-display flex-1">{item.label}</span>
                <span className="label-tech">{item.code}</span>
              </NavLink>
            );
          })}
        </nav>

        <button
          onClick={() => setBrandingOpen(true)}
          className="flex items-center gap-2 mx-3 mb-2 px-3 py-2 text-xs text-[var(--text-muted)] hover:text-[var(--tan-bright)] border border-transparent hover:border-[var(--border)]"
        >
          <Settings className="w-3.5 h-3.5" />
          <span className="font-display">Branding</span>
        </button>

        <div className="px-5 py-4 border-t text-xs space-y-1.5" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <Circle
              className={`w-2.5 h-2.5 ${dbDown ? "text-red-500" : "text-emerald-500 pulse-dot"}`}
              fill="currentColor"
            />
            <span className="font-mono-tech" style={{ color: dbDown ? "var(--red-bright)" : "var(--olive-bright)" }}>
              {dbDown ? "DB UNREACHABLE" : "DB ONLINE"}
            </span>
          </div>
          {mapInfo && (
            <div className="font-mono-tech flex items-center gap-2">
              <Radio className="w-3 h-3" style={{ color: "var(--tan-bright)" }} />
              <span style={{ color: "var(--text-muted)" }}>AO:</span>
              <span style={{ color: "var(--text)" }}>{mapInfo.mapName}</span>
            </div>
          )}
          <div className="font-mono-tech text-[10px]" style={{ color: "var(--text-faint)" }}>
            BUILD 1.0 · CLASSIFIED
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        {/* Topbar */}
        <header
          className="h-14 flex items-center justify-between px-4 md:px-8 border-b sticky top-0 z-30 backdrop-blur"
          style={{ borderColor: "var(--border)", background: "rgba(7, 6, 10, 0.78)" }}
        >
          <div className="flex items-center gap-3 md:hidden">
            <Logo branding={branding} size={32} />
            <div className="font-stencil text-base leading-none">{branding.serverName}</div>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <span className="classified">CLASSIFIED</span>
            <span className="label-tech" style={{ color: "var(--tan-bright)" }}>{breadcrumb}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="label-tech hidden sm:inline">INSTANCE</span>
            <select
              value={instance}
              onChange={(e) => setInstance(e.target.value)}
              className="bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] text-sm px-3 py-1.5 font-mono-tech focus:outline-none focus:border-[var(--tan-bright)]"
            >
              {instances.map((i) => (<option key={i} value={i}>{i}</option>))}
            </select>
          </div>
        </header>

        {/* Mobile nav */}
        <div className="md:hidden flex border-b" style={{ borderColor: "var(--border)", background: "rgba(20, 17, 13, 0.9)" }}>
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center gap-1 py-2 text-xs ${
                    isActive ? "text-[var(--tan-bright)] border-b-2 border-[var(--amber-bright)]" : "text-[var(--text-muted)]"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="font-display">{item.label}</span>
              </NavLink>
            );
          })}
        </div>

        <main className="flex-1 p-4 md:p-8 relative">{children}</main>
      </div>

      <BrandingModal open={brandingOpen} onClose={() => setBrandingOpen(false)} />
    </div>
  );
}
